import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import times from 'lodash/times';
import compact from 'lodash/compact';

import styled from 'styled-components';
import { FormikTextinput } from '@zimpel/common/components/formikAdapters';
import LabeledRow from '@zimpel/common/components/LabeledRow';
import { GridCol, GridRow } from '@zimpel/common/components/Grid';
import Tooltip from '@zimpel/common/components/Tooltip';
import t from '@zimpel/common/helpers/i18n';
import { AddIcon } from '@zimpel/common/components/Icon';
import { SmallBlock } from '@zimpel/common/components/Blocks';
import Label from '@zimpel/common/components/Label';

type Props = {
  name: string;
  label: string;
  maxFields: number;
  contentColumnCount?: number;
  autoFocus?: boolean;
};

const FormikTextInputList: React.FC<Props> = ({
  name, label, maxFields, contentColumnCount, autoFocus = false,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const fields = compact(times(maxFields, (i) => {
    const fieldNumber = i + 1;
    const fieldName = `${name}${fieldNumber}`;

    const nextFieldName = `${name}${fieldNumber + 1}`;
    const nextValue = values[nextFieldName];

    const canAddRow = (nextValue === null || nextValue === undefined) && fieldNumber < maxFields;
    const addRow = () => { setFieldValue(nextFieldName, ''); };

    if (values[fieldName] === null || values[fieldName] === undefined) {
      return null;
    }

    const labelWithNumber = fieldNumber > 1 ? `${fieldNumber}. ${label}` : label;

    return (
      <Field
        fieldName={fieldName}
        label={labelWithNumber}
        canAddRow={canAddRow}
        addRow={addRow}
        contentColumnCount={contentColumnCount}
        key={i}
        autoFocus={autoFocus && fieldNumber === 1}
      />
    );
  }));

  return <>{fields}</>;
};

type FieldProps = {
  fieldName: string;
  label: string;
  canAddRow: boolean;
  addRow: () => void;
  contentColumnCount?: number;
  autoFocus?: boolean;
};
const Field: React.FC<FieldProps> = ({
  fieldName, label, canAddRow, addRow, contentColumnCount, autoFocus = false,
}) => {
  const [isHovered, setHovered] = useState(false);

  return (
    <SmallBlock>
      <GridRow
        data-test="labeled-row"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <LabeledRow
          label={<Label htmlFor={fieldName}>{label}</Label>}
          contentColumnCount={contentColumnCount || 7}
        >
          <FormikTextinput id={fieldName} name={fieldName} autoFocus={autoFocus} />
        </LabeledRow>
        {canAddRow && (
          <GridCol $width={1}>
            {canAddRow && isHovered && <AddButton addRow={addRow} />}
          </GridCol>
        )}
      </GridRow>
    </SmallBlock>
  );
};

const AddIconWrapper = styled.span`
  display: inline-block;
  font-size: 16px;
  margin-top: 1px;
`;

type AddButtonProps = {
  addRow: () => void;
}
const AddButton: React.FC<AddButtonProps> = ({ addRow }) => (
  <Tooltip content={t('add_input_button.tooltip')}>
    <AddIconWrapper>
      <AddIcon
        size="lg"
        onClick={addRow}
        data-test="labeled-row-add-button"
        leftMargin
        rightMargin={false}
      />
    </AddIconWrapper>
  </Tooltip>
);

export default FormikTextInputList;
